import React from 'react';
import { Box, ButtonBase, Chip } from '@mui/material';

import { r, color, radius, tabularNums } from '../../../theme/tokens';
import StackDisclaimer from '../../common/StackDisclaimer';
import { RENDER_STATE } from '../renderDecision';
import {
  SECTION_LABEL_RECOMMENDED,
  FRESHNESS_PREFIX,
  FRESHNESS_UPDATED_PREFIX,
  FRESHNESS_LINK,
  PAUSED_BADGE,
  PAUSED_LEAD,
  PAUSED_UPDATED_PREFIX,
} from '../copy';

/**
 * The contained "Recommended for you" zone — §1.
 *
 * §1's hierarchy intent, verbatim and worth keeping in front of whoever edits
 * this next: "do not 'simplify' this. The band makes Recommended a distinct
 * zone; the smaller eyebrow label keeps it subordinate to My stacks. Distinct,
 * not dominant. Removing the band or promoting the label breaks the intent."
 *
 * So the label is an eyebrow (11px uppercase), not a heading, and it is not an
 * `<h2>` — "My stacks" is the page's only section-level heading. The band is
 * the surface that makes this region legible as a region; the label is
 * deliberately the quietest thing in it.
 *
 * Whether the band renders at all is `resolveBand`'s call, not this
 * component's. It is not rendered by the caller when both stacks resolve to
 * not-rendered, which is why there is no empty state anywhere in this file:
 * §2 forbids an empty section and a placeholder card alike.
 */

const formatDate = (date) => {
  if (!date || !(date instanceof Date) || date.getTime() === 0) return null;
  try {
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    return null;
  }
};

/**
 * The band's freshness date is the most recent `updatedAt` across the stacks
 * it is actually showing. §11 filed this as backend work; it is not —
 * `fetchSystemStacks` already reads `updatedAt` off each system-stack document.
 */
export function bandFreshness(entries) {
  const dates = entries
    .filter((entry) => entry.state !== RENDER_STATE.NOT_RENDERED)
    .map((entry) => entry.stack?.jsUpdatedAt)
    .filter((date) => date instanceof Date && date.getTime() > 0);

  if (!dates.length) return null;
  return new Date(Math.max(...dates.map((date) => date.getTime())));
}

export default function RecommendedBand({
  entries,
  onUpdateProfile,
  renderCard,
  paused = false,
  hasStackAccess = () => true,
}) {
  // While paused this slot carries `PAUSED_LEAD` instead (below). The normal
  // line is wrong twice over for a lapsed member: "Rebuilt whenever you update
  // your medications or diet" is untrue of a membership where rebuilding has
  // stopped, and its "Update profile ›" link competed with the cards' own
  // controls for the same click (Tanu, 2026-08-28).
  const date = formatDate(bandFreshness(entries));
  const freshness = paused ? null : date;
  const visible = entries.filter((entry) => entry.state !== RENDER_STATE.NOT_RENDERED);

  // `PAUSED` and `LOCKED` return their own card before `RecommendedStackCard`
  // reads `stack`, so neither puts a supplement row on screen.
  //
  // `entry.state` alone is not enough. Membership is decided outside the
  // render decision: the dashboard wraps each card in `ProFeature`, which swaps
  // in the paused card or the upgrade prompt while the entry here still reads
  // as a full stack. `hasStackAccess` is that same predicate, so a lapsed or
  // never-subscribed reader does not get a disclaimer for rows they are not
  // shown.
  const showsSupplements = visible.some(
    (entry) =>
      entry.state !== RENDER_STATE.PAUSED &&
      entry.state !== RENDER_STATE.LOCKED &&
      entry.stack?.supplements?.length > 0 &&
      hasStackAccess(entry.stackType),
  );

  return (
    <Box
      component="section"
      aria-label={SECTION_LABEL_RECOMMENDED}
      data-testid="recommended-band"
      sx={{
        width: '100%',
        backgroundColor: color.surface,
        border: `${r(1)} solid ${color.line}`,
        borderRadius: { xs: '0.875rem', md: radius.lg }, // 14px mobile, 16px desktop
        padding: {
          xs: '0.875rem',
          md: '1.25rem 1.375rem 1.375rem', // 20/22/22, per §1
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <Box
          component="span"
          data-testid="recommended-label"
          sx={{
            display: 'block',
            fontSize: '0.6875rem', // 11px
            fontWeight: 700,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: color.inkMuted,
          }}
        >
          {SECTION_LABEL_RECOMMENDED}
        </Box>

        {/* §2, lapsed members: "Badge on the section label reads 'Paused'."
            On the label, not on each card — the cards carry the CTA, and the
            band is what is actually paused. Neutral surface rather than the
            accent used by the cards' own "Recommended" badge: this one states
            a condition, it does not invite a click. */}
        {paused && (
          <Chip
            label={PAUSED_BADGE}
            size="small"
            data-testid="paused-badge"
            sx={{
              height: '1.25rem',
              backgroundColor: color.paper,
              border: `${r(1)} solid ${color.line}`,
              color: color.inkSoft,
              fontSize: '0.625rem', // matches the eyebrow's register, one step down
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          />
        )}
      </Box>

      {/* The paused line sits in the freshness line's slot, directly under the
          label — a line about the recommendations belongs with them, not under
          the page greeting where it shipped first (Tanu, 2026-08-28). The date
          is appended only when there is one, for the same reason the freshness
          line is omitted on a first landing: no claim without a value behind
          it. */}
      {paused && (
        <Box
          data-testid="paused-lead"
          sx={{
            marginTop: '0.375rem',
            fontSize: '0.75rem',
            color: color.inkSoft,
            lineHeight: 1.5,
            ...tabularNums,
          }}
        >
          {PAUSED_LEAD}
          {date && ` ${PAUSED_UPDATED_PREFIX} ${date}`}
        </Box>
      )}

      {/* Only when something has actually been rebuilt. On a first landing
          both cards are locked, `bandFreshness` is null, and the line would
          claim a freshness that does not exist while offering an "Update
          profile" link the locked cards' own CTAs already cover — confusing,
          and not what the design file draws for that state (Tanu,
          2026-08-27). Render 4a keeps it, because there it is true. */}
      {freshness && (
      <Box
        data-testid="freshness-line"
        sx={{
          marginTop: '0.375rem',
          fontSize: '0.75rem',
          color: color.inkSoft,
          lineHeight: 1.5,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '0.25rem',
          ...tabularNums,
        }}
      >
        <span>{FRESHNESS_PREFIX}</span>
        <span>
          · {FRESHNESS_UPDATED_PREFIX} {freshness}
        </span>
        <span aria-hidden="true">·</span>
        <ButtonBase
          onClick={onUpdateProfile}
          sx={{
            minHeight: '2.75rem',
            paddingInline: '0.25rem',
            // `linkHover`, not `accent`: accent as text on this band surface
            // is 3.28:1 and fails AA (Tanu's ruling, 2026-08-26).
            color: color.linkHover,
            fontSize: 'inherit',
            fontWeight: 600,
            '&:focus-visible': {
              outline: `${r(2)} solid ${color.accent}`,
              outlineOffset: r(2),
            },
          }}
        >
          {FRESHNESS_LINK}
        </ButtonBase>
      </Box>
      )}

      <Box
        sx={{
          marginTop: '1rem',
          display: 'grid',
          // 1-up mobile, 2-up desktop — §8. Never re-sorted by the My stacks
          // sort control (§5).
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: '1rem',
          alignItems: 'stretch',
        }}
      >
        {visible.map((entry, index) => (
          <React.Fragment key={entry.stackType}>{renderCard(entry, index)}</React.Fragment>
        ))}
      </Box>

      {/* One disclaimer for the band, inside its container and below both
          cards (Tanu, 2026-09-21 — STACK-541). The 2026-09-14 ruling put one
          on each card; seen on the preview, that set the same sentence twice
          side by side, so the cards carry none and this line covers the pair.

          Guarded on a supplement row actually being on screen: a paused or
          locked card withholds the rows entirely, so a band showing only those
          would be disclaiming a stack the reader cannot see — the defect
          PR 326 fixed on the tab this band replaced. */}
      {showsSupplements && <StackDisclaimer sx={{ mt: 1.5 }} />}
    </Box>
  );
}
